<template>
  <button :class="classes" :style="styles" type="button" @click="onClick">
    <AppButtonIcon
      v-if="showIconBefore"
      :button-height
      :button-size="size"
      :height
      :icon-density
      :icon-name="iconBefore"
      :loading
      :width
      class="abn-IconBefore"
    />

    <slot />

    <AppButtonIcon
      v-if="showIconAfter || (!showIconBefore && !showIconAfter && loading)"
      :button-height
      :button-size="size"
      :height
      :icon-density
      :icon-name="iconAfter"
      :loading
      :width
    />
  </button>
</template>

<script setup>
import { computed, useSlots } from 'vue'

import {
  BUTTON_ICON_DENSITIES,
  BUTTON_SIZES_NEXT,
  BUTTON_VARIANTS
} from '@/utils/components-configurations/app-button'
import { includesValue } from '@/utils/prop-validators'

import AppButtonIcon from '@/components/ui/AppButtonNext/AppButtonIcon'

defineOptions({
  name: 'AppButtonNext'
})

const props = defineProps({
  loading: {
    type: Boolean
  },

  disabled: {
    type: Boolean
  },

  variant: {
    type: String,
    default: BUTTON_VARIANTS.PRIMARY,
    validator: v => includesValue(v, BUTTON_VARIANTS)
  },

  size: {
    type: String,
    default: BUTTON_SIZES_NEXT.MD,
    validator: v => includesValue(v, BUTTON_SIZES_NEXT)
  },

  color: {
    type: String,
    default: '--primary-color-next'
  },

  iconBefore: {
    type: String,
    default: ''
  },

  iconAfter: {
    type: String,
    default: ''
  },

  width: {
    type: [String, Number],
    default: ''
  },

  height: {
    type: [String, Number],
    default: ''
  },

  iconDensity: {
    type: String,
    default: BUTTON_ICON_DENSITIES.DEFAULT,
    validator: v => includesValue(v, BUTTON_ICON_DENSITIES)
  },

  noPadding: {
    type: Boolean
  }
})

const emit = defineEmits({ click: null })

const onClick = eventData => {
  if (!props.disabled && !props.loading) {
    emit('click', eventData)
  }
}

const { XS, SM, MD, LG, XL, XXL } = BUTTON_SIZES_NEXT

const buttonHeight = computed(() => {
  const heightBySize = {
    [XS]: 24,
    [SM]: 28,
    [MD]: 32,
    [LG]: 36,
    [XL]: 40,
    [XXL]: 52
  }

  const height = heightBySize[props.size]

  if (!height) {
    throw new Error(`Seems that you passed incorrect size prop: ${props.size}`)
  }

  return height
})

const styles = computed(() => {
  return {
    '--abn-color-100': `var(${props.color}-100)`,
    '--abn-color-500': `var(${props.color}-500)`,
    '--abn-color-600': `var(${props.color}-600)`,
    '--abn-color-700': `var(${props.color}-700)`,
    '--abn-height': `${buttonHeight.value}px`
  }
})

const slots = useSlots()

const classes = computed(() => {
  const isContentEmpty = !slots.default && !showIconBefore.value && !showIconAfter.value
  const isWithBeforeIcon = showIconBefore.value && !showIconAfter.value
  const isWithAfterIcon =
    (showIconAfter.value && !showIconBefore.value) ||
    (!showIconBefore.value && !showIconAfter.value && props.loading)
  const isWithBothIcons = showIconBefore.value && showIconAfter.value

  return {
    'abn-Button': true,
    [`abn-Button-${props.variant}`]: true,
    [`abn-Button-${props.size}`]: true,
    'abn-Button-wo-content': isContentEmpty,
    'abn-Button-only-icon':
      !slots.default && (isWithBeforeIcon || isWithAfterIcon || isWithBothIcons),
    'abn-Button-loading': props.loading,
    'abn-Button-disabled': props.disabled,
    'abn-Button-no-padding': props.noPadding,
    'abn-Button-with-before-icon': isWithBeforeIcon,
    'abn-Button-with-after-icon': isWithAfterIcon,
    'abn-Button-both-icons': isWithBothIcons
  }
})

const showIconBefore = computed(() => Boolean(props.iconBefore))

const showIconAfter = computed(() => Boolean(props.iconAfter))
</script>

<style lang="scss" scoped>
@mixin abn-interaction($hoverBg, $hoverColor, $activeBg, $activeColor, $activeShadow: null) {
  &:not(.abn-Button-disabled) {
    &:hover {
      background: $hoverBg;
      color: $hoverColor;
    }
    &:active {
      background: $activeBg;
      color: $activeColor;

      @if $activeShadow {
        box-shadow: $activeShadow;
      }
    }
  }
}

@mixin abn-padding($highPadding: 0, $lowPadding: 0) {
  --abn-high-padding: #{$highPadding};
  --abn-low-padding: #{$lowPadding};
}

@mixin abn-colors($bgColor, $fontColor, $boxShadow: null) {
  --abn-background: #{$bgColor};
  --abn-color: #{$fontColor};

  @if $boxShadow {
    box-shadow: $boxShadow;
  }
}

.abn-Button {
  display: flex;
  align-items: center;
  justify-content: var(--justify-content, center);
  border-width: 0;
  max-width: 100%;
  font-size: $fs-14;
  font-weight: fw('semi-bold');
  border-radius: $border-radius-sm-next;
  outline: none !important;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.1s ease-out, color 0.15s ease-out,
    box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
  transition-duration: 0.1s, 0.15s;
  width: auto;
  background-color: transparent;
  text-align: center;
  gap: var(--abn-gap, 4px);
  font-family: $system-ui;
  background: var(--abn-background);
  color: var(--abn-color);
  padding: 0 var(--abn-high-padding);
  height: var(--abn-height);

  &-xs,
  &-sm,
  &-md {
    @include abn-padding(8px, 4px);
  }

  &-lg,
  &-xl {
    @include abn-padding(12px, 6px);
  }

  &-lg {
    font-size: $fs-16;
  }

  &-xl {
    font-size: $fs-16;
  }

  &-xxl {
    @include abn-padding(16px, 8px);
    font-size: $fs-18;
  }

  &-with-before-icon {
    padding-inline: var(--abn-low-padding) var(--abn-high-padding);
  }

  &-with-after-icon {
    padding-inline: var(--abn-high-padding) var(--abn-low-padding);
  }

  &-both-icons {
    padding-inline: var(--abn-low-padding);
  }

  &-wo-content {
    padding-inline: 0;
    width: var(--abn-height);
  }

  &-only-icon {
    padding-inline: 0;
    min-width: var(--abn-height);
  }

  &-only-icon#{&}-both-icons {
    padding-inline: var(--abn-low-padding);
  }

  &-loading {
    pointer-events: none;
  }

  &-primary {
    @include abn-colors(var(--abn-color-600), var(--white-color));

    @include abn-interaction(
      var(--abn-color-700),
      var(--white-color),
      var(--abn-color-500),
      var(--white-color)
    );
  }

  &-secondary {
    @include abn-colors(var(--gray-blue-0), var(--abn-color-600));

    @include abn-interaction(
      var(--gray-blue-100),
      var(--abn-color-600),
      var(--white-color),
      var(--abn-color-600),
      0 0 0 1px var(--abn-color-100)
    );
  }

  &-secondary-gray {
    @include abn-colors(var(--gray-blue-0), var(--gray-blue-800));

    @include abn-interaction(
      var(--gray-blue-100),
      var(--gray-blue-800),
      var(--white-color),
      var(--gray-blue-800),
      0 0 0 1px var(--primary-color-next-100)
    );
  }

  &-tertiary {
    @include abn-colors(transparent, var(--abn-color-600));

    @include abn-interaction(
      var(--gray-blue-0),
      var(--abn-color-700),
      var(--white-color),
      var(--abn-color-500),
      0 0 0 1px var(--abn-color-100)
    );
  }

  &-tertiary-gray {
    @include abn-colors(var(--white-color), var(--gray-blue-800));

    @include abn-interaction(
      var(--gray-blue-0),
      var(--gray-blue-800),
      var(--white-color),
      var(--gray-blue-800),
      0 0 0 1px var(--gray-100)
    );
  }

  &-link {
    @include abn-colors(transparent, var(--abn-color-600));

    @include abn-interaction(transparent, var(--abn-color-700), transparent, var(--abn-color-500));
  }

  &-link-gray {
    @include abn-colors(var(--white-color), var(--gray-blue-600));

    @include abn-interaction(
      var(--white-color),
      var(--gray-blue-700),
      var(--white-color),
      var(--gray-blue-500)
    );
  }

  &-disabled {
    cursor: not-allowed;
  }

  &-disabled#{&}-primary {
    @include abn-colors(var(--gray-blue-0), var(--gray-blue-400));
  }

  &-disabled#{&}-secondary {
    @include abn-colors(var(--white-color), var(--gray-blue-400), 0 0 0 1px var(--abn-color-100));
  }

  &-disabled#{&}-tertiary {
    @include abn-colors(transparent, var(--gray-400));
  }

  &-disabled#{&}-link {
    @include abn-colors(transparent, var(--gray-blue-300));
  }

  &-disabled#{&}-secondary-gray {
    @include abn-colors(var(--white-color), var(--gray-blue-400), 0 0 0 1px var(--gray-100));
  }

  &-disabled#{&}-tertiary-gray {
    @include abn-colors(var(--white-color), var(--gray-blue-400));
  }

  &-disabled#{&}-link-gray {
    @include abn-colors(var(--white-color), var(--gray-blue-300));
  }
}

.abn-Button-no-padding {
  @include abn-padding();
}

.abn-IconBefore {
  --abi-transform: var(--abn-bi-transform, none);
  --abi-transition: var(--abn-bi-transition, none);
  --abi-color: var(--abn-bi-color, inherit);
}
</style>
